import React, { useEffect, useRef, useState, useCallback } from 'react';
import { TelemetryLapData } from '../../models/TelemetryData';
import { SectorDistances } from '../../utils/sectorDistances';
import { useTelemetryCharts, CHART_CONFIGS } from '../../hooks/useTelemetryCharts';
import { getLapColor, REF_LAP_ID } from './LapSelector';
import { TrackTurn } from '../../utils/turnMarkers';

interface TelemetryChartsProps {
  lapDataMap: Map<string, TelemetryLapData>;
  checkedLapIds: string[];
  baselineLapId: string | null;
  channels: Record<string, boolean>;
  trackLength: number;
  onChartClick: (distanceMarker: number) => void;
  onZoomChange?: (min: number | null, max: number | null) => void;
  lapLabels?: Map<string, string>;
  allLapIds?: string[];
  sectorDistances?: SectorDistances | null;
  currentDistanceMarker?: number | null;
  zoomRange?: { min: number; max: number } | null;
  suppressEscape?: boolean;
  /**
   * Left/right arrows step the cursor (and swallow the key). Off on a scrolling page, where
   * the arrows must still scroll it.
   */
  arrowKeyNavigation?: boolean;
  /** Official turns to mark ("Turn N" on the speed chart). Undefined leaves the charts as they were; [] clears them. */
  turns?: TrackTurn[];
}

const CHANNEL_TO_CHART: Record<string, string> = {
  brake: 'brakeChart',
  throttle: 'throttleChart',
  speed: 'speedChart',
  steering: 'steeringChart',
  rpm: 'rpmChart',
  gear: 'gearChart',
};

function formatChartValue(chartId: string, val: number): string {
  if (chartId === 'speedChart') return `${Math.round(val * 0.621371)} mph`;
  if (chartId === 'steeringChart') return `${val.toFixed(1)}°`;
  if (chartId === 'gearChart') return `Gear ${Math.round(val)}`;
  if (chartId === 'rpmChart') return `${Math.round(val).toLocaleString()} rpm`;
  if (chartId === 'deltaChart') return `${val >= 0 ? '+' : ''}${val.toFixed(3)}s`;
  return `${Math.round(val)}%`; // brake, throttle
}

const TelemetryCharts: React.FC<TelemetryChartsProps> = ({
  lapDataMap,
  checkedLapIds,
  baselineLapId,
  channels,
  trackLength,
  onChartClick,
  onZoomChange,
  lapLabels,
  allLapIds,
  sectorDistances,
  currentDistanceMarker,
  zoomRange,
  suppressEscape,
  arrowKeyNavigation = true,
  turns,
}) => {
  // Per-lap values at cursor — updated via the hook's onCursorValues callback
  const [cursorValues, setCursorValues] = useState<Map<string, Map<string, number>> | null>(null);

  const handleCursorValues = useCallback((values: Map<string, Map<string, number>>) => {
    setCursorValues(values);
  }, []);

  const { createChart, updateDatasets, chartsRef, clearAnnotations, setSectorAnnotations, setTurnAnnotations, navigateByArrowKey, addVerticalLine, setChartZoomRange } = useTelemetryCharts({
    onChartClick,
    onZoomChange,
    onCursorValues: handleCursorValues,
  });
  const initialized = useRef(false);
  const firstDatasetLoad = useRef(true);

  // Initialize charts once when we have data
  useEffect(() => {
    if (lapDataMap.size === 0 || initialized.current) return;

    const firstLapData = lapDataMap.values().next().value;
    if (!firstLapData) return;

    const distanceMarkers = Object.keys(firstLapData.t);

    for (const config of CHART_CONFIGS) {
      createChart(config.id, config.id, distanceMarkers, trackLength);
    }
    createChart('deltaChart', 'deltaChart', distanceMarkers, trackLength);

    initialized.current = true;
  }, [lapDataMap, trackLength, createChart]);

  // Update datasets when checked laps change
  useEffect(() => {
    if (!initialized.current) return;
    const animateIn = firstDatasetLoad.current;
    firstDatasetLoad.current = false;
    updateDatasets(lapDataMap, checkedLapIds, baselineLapId, lapLabels, allLapIds, animateIn);
  }, [lapDataMap, checkedLapIds, baselineLapId, updateDatasets, lapLabels, allLapIds]);

  // Update sector annotations when distances change
  useEffect(() => {
    if (!initialized.current) return;
    setSectorAnnotations(sectorDistances ?? null);
  }, [sectorDistances, setSectorAnnotations]);

  // Official turns. lapDataMap is a dependency so turns that arrived before the charts existed
  // are drawn once they do.
  useEffect(() => {
    if (!initialized.current || turns === undefined) return;
    setTurnAnnotations(turns, trackLength);
  }, [turns, trackLength, setTurnAnnotations, lapDataMap]);

  // Sync chart cursor line with external distance marker (e.g. during playback)
  useEffect(() => {
    if (!initialized.current || currentDistanceMarker == null) return;
    addVerticalLine(currentDistanceMarker);
  }, [currentDistanceMarker, addVerticalLine]);

  // Programmatically zoom charts when zoomRange changes (e.g. tip click)
  useEffect(() => {
    if (!initialized.current || !zoomRange) return;
    setChartZoomRange(zoomRange.min, zoomRange.max);
  }, [zoomRange, setChartZoomRange]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !suppressEscape) {
        // Reset zoom range on all charts first (no update yet), then let
        // clearAnnotations do a single chart.update('none') per chart.
        // A second update would re-trigger the annotation proxy and infinite-loop.
        Object.values(chartsRef.current).forEach((chart) => {
          chart.options.scales!.x!.min = 0;
          chart.options.scales!.x!.max = trackLength;
        });
        clearAnnotations();
        onZoomChange?.(null, null);
        // Escaping the zoom must not blank the playhead. If a marker is still
        // active (a trace click / playback position), re-draw the blue time/dist
        // cursor line at it — clearAnnotations dropped it, and the sync effect
        // won't re-fire since currentDistanceMarker is unchanged. Otherwise clear
        // the header cursor values.
        if (currentDistanceMarker != null) {
          addVerticalLine(currentDistanceMarker);
        } else {
          setCursorValues(null);
        }
      } else if (!arrowKeyNavigation) {
        return;
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        navigateByArrowKey('right', event.shiftKey);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        navigateByArrowKey('left', event.shiftKey);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [chartsRef, trackLength, clearAnnotations, navigateByArrowKey, onZoomChange, suppressEscape, currentDistanceMarker, addVerticalLine, arrowKeyNavigation]);

  return (
    <div className="telemetry-charts">
      {CHART_CONFIGS.map((config) => {
        const channelKey = Object.entries(CHANNEL_TO_CHART).find(
          ([, chartId]) => chartId === config.id
        )?.[0];
        const isVisible = channelKey ? channels[channelKey] !== false : true;

        return (
          <div
            key={config.id}
            className={`chart-card ${isVisible ? '' : 'hidden'}`}
            data-ch={channelKey || config.id}
          >
            <div className="chart-card-header">
              <span className="chart-label">{config.title}</span>
              <div className="chart-values">
                {cursorValues?.get(config.id) && checkedLapIds.map((lapId) => {
                  const lapLabel = lapLabels?.get(lapId) || lapId.substring(0, 4);
                  const val = cursorValues.get(config.id)?.get(lapLabel);
                  if (val === undefined) return null;
                  const color = getLapColor(lapId, allLapIds || checkedLapIds);
                  const showLabel = checkedLapIds.length <= 4;
                  return (
                    <span key={lapId} className="chart-val-group">
                      <span className="chart-val-dot" style={{ background: color }} />
                      {showLabel && <span className="chart-val-lap">{lapLabel}</span>}
                      <span className="chart-val-num" style={{ color }}>{formatChartValue(config.id, val)}</span>
                    </span>
                  );
                })}
              </div>
            </div>
            <div className="chart-body">
              <canvas id={config.id} />
            </div>
          </div>
        );
      })}

      <div className={`chart-card delta-chart ${((!checkedLapIds.includes(REF_LAP_ID) && checkedLapIds.length < 2) || channels.delta === false) ? 'hidden' : ''}`}>
        <div className="chart-card-header">
          <span className="chart-label">DELTA</span>
          <div className="chart-values">
            {(() => {
              const hasRef = checkedLapIds.includes(REF_LAP_ID);
              if (hasRef) {
                return checkedLapIds
                  .filter((id) => id !== REF_LAP_ID)
                  .map((lapId) => {
                    const lapLabel = lapLabels?.get(lapId) || lapId.substring(0, 4);
                    const val = cursorValues?.get('deltaChart')?.get(lapLabel);
                    if (val === undefined) return null;
                    const color = getLapColor(lapId, allLapIds || checkedLapIds);
                    return (
                      <span key={lapId} className="chart-val-group">
                        <span className="chart-val-dot" style={{ background: color }} />
                        <span className="chart-val-num" style={{ color: val >= 0 ? '#ff3333' : '#2ecc71' }}>
                          {formatChartValue('deltaChart', val)}
                        </span>
                      </span>
                    );
                  });
              }
              // Fallback: original single delta display
              const val = cursorValues?.get('deltaChart')?.get('Delta');
              if (val === undefined) return null;
              return (
                <span className="chart-val-group">
                  <span className="chart-val-dot" style={{ background: '#ff9800' }} />
                  <span className="chart-val-num" style={{ color: val >= 0 ? '#ff3333' : '#2ecc71' }}>
                    {formatChartValue('deltaChart', val)}
                  </span>
                </span>
              );
            })()}
          </div>
        </div>
        <div className="chart-body">
          <canvas id="deltaChart" />
        </div>
      </div>
    </div>
  );
};

export default TelemetryCharts;
